import { useState } from 'react';
import { Archive, Plus } from 'lucide-react';
import type { StorageLocation, CreateStorageLocationInput } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import {
  StorageLocationList,
  StorageLocationForm,
  useStorageLocations,
  useCreateStorageLocation,
  useUpdateStorageLocation,
} from '@/features/storage';

export function StorageLocationsPage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StorageLocation | null>(null);

  const { data, isLoading } = useStorageLocations();
  const createMutation = useCreateStorageLocation();
  const updateMutation = useUpdateStorageLocation();

  const locations = data || [];
  const hasLocations = locations.length > 0 || isLoading;

  const handleOpenForm = (location?: StorageLocation) => {
    setEditingLocation(location || null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingLocation(null);
  };

  const handleSubmit = async (formData: CreateStorageLocationInput) => {
    try {
      if (editingLocation) {
        await updateMutation.mutateAsync({
          id: editingLocation.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      handleCloseForm();
    } catch {
      // Error is handled by mutation
    }
  };

  return (
    <div className="p-4">
      {/* Page header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Storage Locations</h1>
          <p className="text-sm text-foreground-muted">
            Organize where your filament spools are stored
          </p>
        </div>
        <Button
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => handleOpenForm()}
        >
          Add Location
        </Button>
      </div>

      {/* Content */}
      <div className="rounded border border-border bg-background-subtle p-4">
        {hasLocations ? (
          <StorageLocationList onEdit={handleOpenForm} />
        ) : (
          <EmptyState
            icon={<Archive className="h-10 w-10" />}
            title="No storage locations yet"
            description="Create storage locations to organize your filament spools. Add shelves, bins, dryers, or any other storage areas."
            actionLabel="Add Location"
            onAction={() => handleOpenForm()}
          />
        )}
      </div>

      {/* Form slide-over */}
      <StorageLocationForm
        open={formOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        location={editingLocation}
        loading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}
